export type LanguageCode = "en" | "ca" | "es" | "fr" | "it" | "de" | "pt-BR" | "pt-PT" | "nl" | "sv" | "pl" | "no";

export const LANGUAGES: Record<LanguageCode, string> = {
  en: "English",
  ca: "Català",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  de: "Deutsch",
  "pt-BR": "Português (Brasil)",
  "pt-PT": "Português (Portugal)",
  nl: "Nederlands",
  sv: "Svenska",
  pl: "Polski",
  no: "Norsk"
};

type Translations = {
  [K in LanguageCode]: {
    settings: {
      title: string;
      description: string;
      type: string;
      typeSelect: string;
      colors: string;
      foreground: string;
      background: string;
      logo: string;
      logoPlaceholder: string;
      autoFavicon: string;
      useSocialIcon: string;
      useCryptoIcon: string;
      size: string;
      logoSize: string;
    };
    preview: {
      title: string;
      description: string;
      rawText: string;
      downloadPNG: string;
      qrSize: string;
      share: string;
      urlCopied: string;
    };
    types: {
      url: string;
      social: string;
      vcard: string;
      phone: string;
      sms: string;
      email: string;
      wifi: string;
      crypto: string;
      geo: string;
      event: string;
      text: string;
      mecard: string;
    };
    forms: {
      // URL form
      urlPlaceholder: string;
      
      // vCard form
      firstName: string;
      lastName: string;
      phone: string;
      email: string;
      organization: string;
      organizationOptional: string;
      street: string;
      streetOptional: string;
      city: string;
      cityOptional: string;
      state: string;
      stateOptional: string;
      zip: string;
      zipOptional: string;
      country: string;
      countryOptional: string;
      
      // Phone form
      phoneNumber: string;
      phonePlaceholder: string;
      
      // SMS form
      message: string;
      messageOptional: string;
      
      // Email form
      emailAddress: string;
      subject: string;
      subjectOptional: string;
      body: string;
      bodyOptional: string;
      
      // WiFi form
      networkName: string;
      password: string;
      encryption: string;
      encryptionType: string;
      selectEncryption: string;
      wpaWpa2: string;
      wep: string;
      none: string;
      
      // Crypto form
      cryptocurrency: string;
      selectCrypto: string;
      bitcoin: string;
      ethereum: string;
      walletAddress: string;
      amount: string;
      amountOptional: string;
      label: string;
      labelOptional: string;
      
      // Geo form
      latitude: string;
      longitude: string;
      
      // Event form
      eventTitle: string;
      startDate: string;
      endDate: string;
      description: string;
      descriptionOptional: string;
      
      // Text form
      text: string;
      
      // MeCard form
      name: string;
      emailOptional: string;
      
      // Social form
      socialPlatform: string;
      selectPlatform: string;
      username: string;
      usernamePlaceholder: string;
      platforms: {
        x: string;
        instagram: string;
        facebook: string;
        linkedin: string;
        github: string;
        tiktok: string;
        youtube: string;
        twitch: string;
        bluesky: string;
      };
    };
  };
};

export const translations: Translations = {
  en: {
    settings: {
      title: "QR Code Settings",
      description: "Customize your QR code appearance and content.",
      type: "QR Code Type",
      typeSelect: "Select QR code type",
      colors: "Colors",
      foreground: "Foreground",
      background: "Background",
      logo: "Logo URL (Optional)",
      logoPlaceholder: "https://example.com/logo.png",
      autoFavicon: "Use website favicon automatically",
      useSocialIcon: "Use social media icon",
      useCryptoIcon: "Use cryptocurrency icon",
      size: "QR Code Size",
      logoSize: "Logo Size",
    },
    preview: {
      title: "Preview",
      description: "Preview your QR code and download it",
      rawText: "Raw QR Code Text",
      downloadPNG: "Download PNG",
      qrSize: "QR Code Size",
      share: "Share URL",
      urlCopied: "URL copied to clipboard!"
    },
    types: {
      url: "URL",
      social: "Social Media Profile",
      vcard: "Contact (vCard)",
      phone: "Phone Number",
      sms: "SMS",
      email: "Email",
      wifi: "Wi-Fi Network",
      crypto: "Cryptocurrency",
      geo: "Geographic Location",
      event: "Calendar Event",
      text: "Plain Text",
      mecard: "Contact (MeCard)",
    },
    forms: {
      urlPlaceholder: "https://example.com",
      firstName: "First Name",
      lastName: "Last Name",
      phone: "Phone",
      email: "Email",
      organization: "Organization",
      organizationOptional: "Organization (Optional)",
      street: "Street Address",
      streetOptional: "Street Address (Optional)",
      city: "City",
      cityOptional: "City (Optional)",
      state: "State",
      stateOptional: "State (Optional)",
      zip: "ZIP Code",
      zipOptional: "ZIP Code (Optional)",
      country: "Country",
      countryOptional: "Country (Optional)",
      phoneNumber: "Phone Number",
      phonePlaceholder: "+1234567890",
      message: "Message",
      messageOptional: "Message (Optional)",
      emailAddress: "Email Address",
      subject: "Subject",
      subjectOptional: "Subject (Optional)",
      body: "Body",
      bodyOptional: "Body (Optional)",
      networkName: "Network Name (SSID)",
      password: "Password",
      encryption: "Encryption",
      encryptionType: "Encryption Type",
      selectEncryption: "Select encryption type",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "None",
      cryptocurrency: "Cryptocurrency",
      selectCrypto: "Select cryptocurrency",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Wallet Address",
      amount: "Amount",
      amountOptional: "Amount (Optional)",
      label: "Label",
      labelOptional: "Message/Label (Optional)",
      latitude: "Latitude",
      longitude: "Longitude",
      eventTitle: "Event Title",
      startDate: "Start Date",
      endDate: "End Date",
      description: "Description",
      descriptionOptional: "Description (Optional)",
      text: "Text",
      name: "Name",
      emailOptional: "Email (Optional)",
      socialPlatform: "Social Platform",
      selectPlatform: "Select platform",
      username: "Username",
      usernamePlaceholder: "your_username",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  ca: {
    settings: {
      title: "Configuració del codi QR",
      description: "Personalitza l'aparença i el contingut del teu codi QR.",
      type: "Tipus de codi QR",
      typeSelect: "Selecciona el tipus de codi QR",
      colors: "Colors",
      foreground: "Primer pla",
      background: "Fons",
      logo: "URL del logotip (Opcional)",
      logoPlaceholder: "https://exemple.com/logo.png",
      autoFavicon: "Usa el favicon del lloc web automàticament",
      useSocialIcon: "Utilitza la icona de xarxa social",
      useCryptoIcon: "Utilitza la icona de criptomoneda",
      size: "Mida del codi QR",
      logoSize: "Mida del logotip",
    },
    preview: {
      title: "Vista prèvia",
      description: "El teu codi QR s'actualitzarà automàticament mentre fas canvis.",
      rawText: "Text del codi QR",
      downloadPNG: "Descarrega PNG",
      qrSize: "Mida del codi QR",
      share: "Comparteix URL",
      urlCopied: "URL copiada al porta-retalls!"
    },
    types: {
      url: "URL",
      social: "Perfil de xarxes socials",
      vcard: "Contacte (vCard)",
      phone: "Telèfon",
      sms: "SMS",
      email: "Correu electrònic",
      wifi: "Xarxa Wi-Fi",
      crypto: "Criptomoneda",
      geo: "Ubicació geogràfica",
      event: "Esdeveniment",
      text: "Text",
      mecard: "Contacte (MeCard)",
    },
    forms: {
      urlPlaceholder: "https://exemple.com",
      firstName: "Nom",
      lastName: "Cognoms",
      phone: "Telèfon",
      email: "Correu electrònic",
      organization: "Organització",
      organizationOptional: "Organització (Opcional)",
      street: "Adreça",
      streetOptional: "Adreça (Opcional)",
      city: "Ciutat",
      cityOptional: "Ciutat (Opcional)",
      state: "Província",
      stateOptional: "Província (Opcional)",
      zip: "Codi Postal",
      zipOptional: "Codi Postal (Opcional)",
      country: "País",
      countryOptional: "País (Opcional)",
      phoneNumber: "Número de telèfon",
      phonePlaceholder: "+34600000000",
      message: "Missatge",
      messageOptional: "Missatge (Opcional)",
      emailAddress: "Adreça de correu",
      subject: "Assumpte",
      subjectOptional: "Assumpte (Opcional)",
      body: "Cos",
      bodyOptional: "Cos (Opcional)",
      networkName: "Nom de la xarxa (SSID)",
      password: "Contrasenya",
      encryption: "Encriptació",
      encryptionType: "Tipus d'encriptació",
      selectEncryption: "Selecciona el tipus d'encriptació",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Cap",
      cryptocurrency: "Criptomoneda",
      selectCrypto: "Selecciona la criptomoneda",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Adreça del moneder",
      amount: "Quantitat",
      amountOptional: "Quantitat (Opcional)",
      label: "Etiqueta",
      labelOptional: "Missatge/Etiqueta (Opcional)",
      latitude: "Latitud",
      longitude: "Longitud",
      eventTitle: "Títol de l'esdeveniment",
      startDate: "Data d'inici",
      endDate: "Data de fi",
      description: "Descripció",
      descriptionOptional: "Descripció (Opcional)",
      text: "Text",
      name: "Nom",
      emailOptional: "Correu electrònic (Opcional)",
      socialPlatform: "Xarxa social",
      selectPlatform: "Selecciona la plataforma",
      username: "Nom d'usuari",
      usernamePlaceholder: "el_teu_usuari",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  es: {
    settings: {
      title: "Configuración del código QR",
      description: "Personaliza la apariencia y el contenido de tu código QR.",
      type: "Tipo de código QR",
      typeSelect: "Selecciona el tipo de código QR",
      colors: "Colores",
      foreground: "Primer plano",
      background: "Fondo",
      logo: "URL del logo (Opcional)",
      logoPlaceholder: "https://ejemplo.com/logo.png",
      autoFavicon: "Usar favicon del sitio web automáticamente",
      useSocialIcon: "Usar icono de red social",
      useCryptoIcon: "Usar ícono de criptomoneda",
      size: "Tamaño del código QR",
      logoSize: "Tamaño del logotipo",
    },
    preview: {
      title: "Vista previa",
      description: "Tu código QR se actualizará automáticamente mientras haces cambios.",
      rawText: "Texto del código QR",
      downloadPNG: "Descargar PNG",
      qrSize: "Tamaño del código QR",
      share: "Compartir URL",
      urlCopied: "¡URL copiada al portapapeles!"
    },
    types: {
      url: "URL",
      social: "Perfil de redes sociales",
      vcard: "Contacto (vCard)",
      phone: "Teléfono",
      sms: "SMS",
      email: "Correo electrónico",
      wifi: "Red Wi-Fi",
      crypto: "Criptomoneda",
      geo: "Ubicación geográfica",
      event: "Evento",
      text: "Texto",
      mecard: "Contacto (MeCard)",
    },
    forms: {
      urlPlaceholder: "https://ejemplo.com",
      firstName: "Nombre",
      lastName: "Apellidos",
      phone: "Teléfono",
      email: "Correo electrónico",
      organization: "Organización",
      organizationOptional: "Organización (Opcional)",
      street: "Dirección",
      streetOptional: "Dirección (Opcional)",
      city: "Ciudad",
      cityOptional: "Ciudad (Opcional)",
      state: "Provincia",
      stateOptional: "Provincia (Opcional)",
      zip: "Código Postal",
      zipOptional: "Código Postal (Opcional)",
      country: "País",
      countryOptional: "País (Opcional)",
      phoneNumber: "Número de teléfono",
      phonePlaceholder: "+34600000000",
      message: "Mensaje",
      messageOptional: "Mensaje (Opcional)",
      emailAddress: "Dirección de correo",
      subject: "Asunto",
      subjectOptional: "Asunto (Opcional)",
      body: "Cuerpo",
      bodyOptional: "Cuerpo (Opcional)",
      networkName: "Nombre de la red (SSID)",
      password: "Contraseña",
      encryption: "Encriptación",
      encryptionType: "Tipo de encriptación",
      selectEncryption: "Selecciona el tipo de encriptación",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Ninguna",
      cryptocurrency: "Criptomoneda",
      selectCrypto: "Selecciona la criptomoneda",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Dirección de la cartera",
      amount: "Cantidad",
      amountOptional: "Cantidad (Opcional)",
      label: "Etiqueta",
      labelOptional: "Mensaje/Etiqueta (Opcional)",
      latitude: "Latitud",
      longitude: "Longitud",
      eventTitle: "Título del evento",
      startDate: "Fecha de inicio",
      endDate: "Fecha de fin",
      description: "Descripción",
      descriptionOptional: "Descripción (Opcional)",
      text: "Texto",
      name: "Nombre",
      emailOptional: "Correo electrónico (Opcional)",
      socialPlatform: "Red social",
      selectPlatform: "Selecciona la plataforma",
      username: "Nombre de usuario",
      usernamePlaceholder: "tu_usuario",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  fr: {
    settings: {
      title: "Paramètres du code QR",
      description: "Personnalisez l'apparence et le contenu de votre code QR.",
      type: "Type de code QR",
      typeSelect: "Sélectionnez le type de code QR",
      colors: "Couleurs",
      foreground: "Premier plan",
      background: "Arrière-plan",
      logo: "URL du logo (Optionnel)",
      logoPlaceholder: "https://exemple.com/logo.png",
      autoFavicon: "Utiliser le favicon du site web automatiquement",
      useSocialIcon: "Utiliser l'icône de réseau social",
      useCryptoIcon: "Utiliser l'icône de cryptomonnaie",
      size: "Taille du code QR",
      logoSize: "Taille du logotipe",
    },
    preview: {
      title: "Aperçu",
      description: "Votre code QR se mettra à jour automatiquement lorsque vous effectuez des modifications.",
      rawText: "Texte du code QR",
      downloadPNG: "Télécharger PNG",
      qrSize: "Taille du code QR",
      share: "Partager l'URL",
      urlCopied: "URL copiée dans le presse-papiers !"
    },
    types: {
      url: "URL",
      social: "Profil de réseau social",
      vcard: "Contact (vCard)",
      phone: "Téléphone",
      sms: "SMS",
      email: "Email",
      wifi: "Réseau Wi-Fi",
      crypto: "Cryptomonnaie",
      geo: "Localisation",
      event: "Événement",
      text: "Texte",
      mecard: "Contact (MeCard)",
    },
    forms: {
      urlPlaceholder: "https://exemple.fr",
      firstName: "Prénom",
      lastName: "Nom",
      phone: "Téléphone",
      email: "Email",
      organization: "Organisation",
      organizationOptional: "Organisation (Optionnel)",
      street: "Adresse",
      streetOptional: "Adresse (Optionnel)",
      city: "Ville",
      cityOptional: "Ville (Optionnel)",
      state: "État/Région",
      stateOptional: "État/Région (Optionnel)",
      zip: "Code Postal",
      zipOptional: "Code Postal (Optionnel)",
      country: "Pays",
      countryOptional: "Pays (Optionnel)",
      phoneNumber: "Numéro de téléphone",
      phonePlaceholder: "+33600000000",
      message: "Message",
      messageOptional: "Message (Optionnel)",
      emailAddress: "Adresse email",
      subject: "Sujet",
      subjectOptional: "Sujet (Optionnel)",
      body: "Corps",
      bodyOptional: "Corps (Optionnel)",
      networkName: "Nom du réseau (SSID)",
      password: "Mot de passe",
      encryption: "Chiffrement",
      encryptionType: "Type de chiffrement",
      selectEncryption: "Sélectionner le type de chiffrement",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Aucun",
      cryptocurrency: "Cryptomonnaie",
      selectCrypto: "Sélectionner la cryptomonnaie",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Adresse du portefeuille",
      amount: "Montant",
      amountOptional: "Montant (Optionnel)",
      label: "Étiquette",
      labelOptional: "Message/Étiquette (Optionnel)",
      latitude: "Latitude",
      longitude: "Longitude",
      eventTitle: "Titre de l'événement",
      startDate: "Date de début",
      endDate: "Date de fin",
      description: "Description",
      descriptionOptional: "Description (Optionnel)",
      text: "Texte",
      name: "Nom",
      emailOptional: "Email (Optionnel)",
      socialPlatform: "Réseau social",
      selectPlatform: "Sélectionner la plateforme",
      username: "Nom d'utilisateur",
      usernamePlaceholder: "votre_utilisateur",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  it: {
    settings: {
      title: "Impostazioni codice QR",
      description: "Personalizza l'aspetto e il contenuto del tuo codice QR.",
      type: "Tipo di codice QR",
      typeSelect: "Seleziona il tipo di codice QR",
      colors: "Colori",
      foreground: "Primo piano",
      background: "Sfondo",
      logo: "URL del logo (Opzionale)",
      logoPlaceholder: "https://esempio.com/logo.png",
      autoFavicon: "Usare favicon del sito web automaticamente",
      useSocialIcon: "Usare l'icona di rete sociale",
      useCryptoIcon: "Usare l'icona di criptomoneta",
      size: "Dimensioni del codice QR",
      logoSize: "Dimensioni del logotipo",
    },
    preview: {
      title: "Anteprima",
      description: "Il tuo codice QR si aggiornerà automaticamente mentre apporti modifiche.",
      rawText: "Testo del codice QR",
      downloadPNG: "Scarica PNG",
      qrSize: "Dimensioni del codice QR",
      share: "Condividi URL",
      urlCopied: "URL copiato negli appunti!"
    },
    types: {
      url: "URL",
      social: "Profilo social",
      vcard: "Contatto (vCard)",
      phone: "Telefono",
      sms: "SMS",
      email: "Email",
      wifi: "Rete Wi-Fi",
      crypto: "Criptovaluta",
      geo: "Posizione geografica",
      event: "Evento",
      text: "Testo",
      mecard: "Contatto (MeCard)",
    },
    forms: {
      urlPlaceholder: "https://esempio.it",
      firstName: "Nome",
      lastName: "Cognome",
      phone: "Telefono",
      email: "Email",
      organization: "Organizzazione",
      organizationOptional: "Organizzazione (Opzionale)",
      street: "Indirizzo",
      streetOptional: "Indirizzo (Opzionale)",
      city: "Città",
      cityOptional: "Città (Opzionale)",
      state: "Stato/Regione",
      stateOptional: "Stato/Regione (Opzionale)",
      zip: "CAP",
      zipOptional: "CAP (Opzionale)",
      country: "Paese",
      countryOptional: "Paese (Opzionale)",
      phoneNumber: "Numero di telefono",
      phonePlaceholder: "+39300000000",
      message: "Messaggio",
      messageOptional: "Messaggio (Opzionale)",
      emailAddress: "Indirizzo email",
      subject: "Oggetto",
      subjectOptional: "Oggetto (Opzionale)",
      body: "Corpo",
      bodyOptional: "Corpo (Opzionale)",
      networkName: "Nome rete (SSID)",
      password: "Password",
      encryption: "Crittografia",
      encryptionType: "Tipo di crittografia",
      selectEncryption: "Seleziona tipo di crittografia",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Nessuna",
      cryptocurrency: "Criptovaluta",
      selectCrypto: "Seleziona criptovaluta",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Indirizzo portafoglio",
      amount: "Importo",
      amountOptional: "Importo (Opzionale)",
      label: "Etichetta",
      labelOptional: "Messaggio/Etichetta (Opzionale)",
      latitude: "Latitudine",
      longitude: "Longitudine",
      eventTitle: "Titolo evento",
      startDate: "Data inizio",
      endDate: "Data fine",
      description: "Descrizione",
      descriptionOptional: "Descrizione (Opzionale)",
      text: "Testo",
      name: "Nome",
      emailOptional: "Email (Opzionale)",
      socialPlatform: "Social network",
      selectPlatform: "Seleziona piattaforma",
      username: "Nome utente",
      usernamePlaceholder: "tuo_username",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  de: {
    settings: {
      title: "QR-Code-Einstellungen",
      description: "Passen Sie das Aussehen und den Inhalt Ihres QR-Codes an.",
      type: "QR-Code-Typ",
      typeSelect: "QR-Code-Typ auswählen",
      colors: "Farben",
      foreground: "Vordergrund",
      background: "Hintergrund",
      logo: "Logo-URL (Optional)",
      logoPlaceholder: "https://beispiel.de/logo.png",
      autoFavicon: "Favicon des Webseiten automatisch verwenden",
      useSocialIcon: "Soziale Medien-Symbol verwenden",
      useCryptoIcon: "Soziale Medien-Symbol verwenden",
      size: "QR-Code-Größe",
      logoSize: "Logo-Größe",
    },
    preview: {
      title: "Vorschau",
      description: "Ihr QR-Code wird automatisch aktualisiert, während Sie Änderungen vornehmen.",
      rawText: "QR-Code-Text",
      downloadPNG: "PNG herunterladen",
      qrSize: "QR-Code-Größe",
      share: "URL teilen",
      urlCopied: "URL in die Zwischenablage kopiert!"
    },
    types: {
      url: "URL",
      social: "Social Media Profil",
      vcard: "Kontakt (vCard)",
      phone: "Telefon",
      sms: "SMS",
      email: "E-Mail",
      wifi: "WLAN-Netzwerk",
      crypto: "Kryptowährung",
      geo: "Geografischer Standort",
      event: "Termin",
      text: "Text",
      mecard: "Kontakt (MeCard)",
    },
    forms: {
      urlPlaceholder: "https://beispiel.de",
      firstName: "Vorname",
      lastName: "Nachname",
      phone: "Telefon",
      email: "E-Mail",
      organization: "Organisation",
      organizationOptional: "Organisation (Optional)",
      street: "Straße",
      streetOptional: "Straße (Optional)",
      city: "Stadt",
      cityOptional: "Stadt (Optional)",
      state: "Bundesland",
      stateOptional: "Bundesland (Optional)",
      zip: "PLZ",
      zipOptional: "PLZ (Optional)",
      country: "Land",
      countryOptional: "Land (Optional)",
      phoneNumber: "Telefonnummer",
      phonePlaceholder: "+49151000000",
      message: "Nachricht",
      messageOptional: "Nachricht (Optional)",
      emailAddress: "E-Mail-Adresse",
      subject: "Betreff",
      subjectOptional: "Betreff (Optional)",
      body: "Inhalt",
      bodyOptional: "Inhalt (Optional)",
      networkName: "Netzwerkname (SSID)",
      password: "Passwort",
      encryption: "Verschlüsselung",
      encryptionType: "Verschlüsselungstyp",
      selectEncryption: "Verschlüsselungstyp auswählen",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Keine",
      cryptocurrency: "Kryptowährung",
      selectCrypto: "Kryptowährung auswählen",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Wallet-Adresse",
      amount: "Betrag",
      amountOptional: "Betrag (Optional)",
      label: "Bezeichnung",
      labelOptional: "Nachricht/Bezeichnung (Optional)",
      latitude: "Breitengrad",
      longitude: "Längengrad",
      eventTitle: "Ereignistitel",
      startDate: "Startdatum",
      endDate: "Enddatum",
      description: "Beschreibung",
      descriptionOptional: "Beschreibung (Optional)",
      text: "Text",
      name: "Name",
      emailOptional: "E-Mail (Optional)",
      socialPlatform: "Soziales Netzwerk",
      selectPlatform: "Plattform auswählen",
      username: "Benutzername",
      usernamePlaceholder: "dein_benutzername",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  "pt-BR": {
    settings: {
      title: "Configurações",
      description: "Personalize seu código QR",
      type: "Tipo",
      typeSelect: "Selecione o tipo",
      colors: "Cores",
      foreground: "Primeiro plano",
      background: "Fundo",
      logo: "Logo",
      logoPlaceholder: "URL do logo",
      autoFavicon: "Usar favicon do site web automaticamente",
      useSocialIcon: "Usar ícone de rede social",
      useCryptoIcon: "Usar ícone de criptomoeda",
      size: "Tamanho do código QR",
      logoSize: "Tamanho do logotipo",
    },
    preview: {
      title: "Visualização",
      description: "Escaneie para testar",
      rawText: "Texto do QR code",
      downloadPNG: "Baixar PNG",
      qrSize: "Tamanho do código QR",
      share: "Compartilhar URL",
      urlCopied: "URL copiada para a área de transferência!"
    },
    types: {
      url: "URL",
      social: "Rede Social",
      vcard: "Contato (vCard)",
      phone: "Telefone",
      sms: "SMS",
      email: "E-mail",
      wifi: "Wi-Fi",
      crypto: "Criptomoeda",
      geo: "Localização",
      event: "Evento",
      text: "Texto",
      mecard: "Contato (MeCard)"
    },
    forms: {
      urlPlaceholder: "https://exemplo.com.br",
      firstName: "Nome",
      lastName: "Sobrenome",
      phone: "Telefone",
      email: "E-mail",
      organization: "Organização",
      organizationOptional: "Organização (Opcional)",
      street: "Endereço",
      streetOptional: "Endereço (Opcional)",
      city: "Cidade",
      cityOptional: "Cidade (Opcional)",
      state: "Estado",
      stateOptional: "Estado (Opcional)",
      zip: "CEP",
      zipOptional: "CEP (Opcional)",
      country: "País",
      countryOptional: "País (Opcional)",
      phoneNumber: "Número de telefone",
      phonePlaceholder: "+5511999999999",
      message: "Mensagem",
      messageOptional: "Mensagem (Opcional)",
      emailAddress: "Endereço de e-mail",
      subject: "Assunto",
      subjectOptional: "Assunto (Opcional)",
      body: "Corpo",
      bodyOptional: "Corpo (Opcional)",
      networkName: "Nome da rede (SSID)",
      password: "Senha",
      encryption: "Criptografia",
      encryptionType: "Tipo de criptografia",
      selectEncryption: "Selecione o tipo de criptografia",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Nenhuma",
      cryptocurrency: "Criptomoeda",
      selectCrypto: "Selecione a criptomoeda",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Endereço da carteira",
      amount: "Quantidade",
      amountOptional: "Quantidade (Opcional)",
      label: "Etiqueta",
      labelOptional: "Mensagem/Etiqueta (Opcional)",
      latitude: "Latitude",
      longitude: "Longitude",
      eventTitle: "Título do evento",
      startDate: "Data de início",
      endDate: "Data de término",
      description: "Descrição",
      descriptionOptional: "Descrição (Opcional)",
      text: "Texto",
      name: "Nome",
      emailOptional: "E-mail (Opcional)",
      socialPlatform: "Rede social",
      selectPlatform: "Selecione a plataforma",
      username: "Nome de usuário",
      usernamePlaceholder: "seu_usuario",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  "pt-PT": {
    settings: {
      title: "Definições",
      description: "Personalize o seu código QR",
      type: "Tipo",
      typeSelect: "Selecione o tipo",
      colors: "Cores",
      foreground: "Primeiro plano",
      background: "Fundo",
      logo: "Logótipo",
      logoPlaceholder: "URL do logótipo",
      autoFavicon: "Usar favicon do site web automaticamente",
      useSocialIcon: "Usar ícone de rede social",
      useCryptoIcon: "Usar ícone de criptomoeda",
      size: "Tamanho do código QR",
      logoSize: "Tamanho do logótipo",
    },
    preview: {
      title: "Pré-visualização",
      description: "Digitalize para testar",
      rawText: "Texto do código QR",
      downloadPNG: "Transferir PNG",
      qrSize: "Tamanho do código QR",
      share: "Compartilhar URL",
      urlCopied: "URL copiada para a área de transferência!"
    },
    types: {
      url: "URL",
      social: "Rede Social",
      vcard: "Contacto (vCard)",
      phone: "Telefone",
      sms: "SMS",
      email: "E-mail",
      wifi: "Wi-Fi",
      crypto: "Criptomoeda",
      geo: "Localização",
      event: "Evento",
      text: "Texto",
      mecard: "Contacto (MeCard)"
    },
    forms: {
      urlPlaceholder: "https://exemplo.pt",
      firstName: "Nome próprio",
      lastName: "Apelido",
      phone: "Telefone",
      email: "E-mail",
      organization: "Organização",
      organizationOptional: "Organização (Opcional)",
      street: "Morada",
      streetOptional: "Morada (Opcional)",
      city: "Cidade",
      cityOptional: "Cidade (Opcional)",
      state: "Distrito",
      stateOptional: "Distrito (Opcional)",
      zip: "Código Postal",
      zipOptional: "Código Postal (Opcional)",
      country: "País",
      countryOptional: "País (Opcional)",
      phoneNumber: "Número de telefone",
      phonePlaceholder: "+351910000000",
      message: "Mensagem",
      messageOptional: "Mensagem (Opcional)",
      emailAddress: "Endereço de e-mail",
      subject: "Assunto",
      subjectOptional: "Assunto (Opcional)",
      body: "Corpo",
      bodyOptional: "Corpo (Opcional)",
      networkName: "Nome da rede (SSID)",
      password: "Palavra-passe",
      encryption: "Encriptação",
      encryptionType: "Tipo de encriptação",
      selectEncryption: "Selecione o tipo de encriptação",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Nenhuma",
      cryptocurrency: "Criptomoeda",
      selectCrypto: "Selecione a criptomoeda",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Endereço da carteira",
      amount: "Quantidade",
      amountOptional: "Quantidade (Opcional)",
      label: "Etiqueta",
      labelOptional: "Mensagem/Etiqueta (Opcional)",
      latitude: "Latitude",
      longitude: "Longitude",
      eventTitle: "Título do evento",
      startDate: "Data de início",
      endDate: "Data de fim",
      description: "Descrição",
      descriptionOptional: "Descrição (Opcional)",
      text: "Texto",
      name: "Nome",
      emailOptional: "E-mail (Opcional)",
      socialPlatform: "Rede social",
      selectPlatform: "Selecione a plataforma",
      username: "Nome de utilizador",
      usernamePlaceholder: "seu_utilizador",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  nl: {
    settings: {
      title: "Instellingen",
      description: "Pas je QR-code aan",
      type: "Type",
      typeSelect: "Selecteer type",
      colors: "Kleuren",
      foreground: "Voorgrond",
      background: "Achtergrond",
      logo: "Logo",
      logoPlaceholder: "Logo URL",
      autoFavicon: "Favicon van de website automatisch gebruiken",
      useSocialIcon: "Soziale Medien-Symbol verwenden",
      useCryptoIcon: "Soziale Medien-Symbol verwenden",
      size: "QR-code grootte",
      logoSize: "Logo grootte",
    },
    preview: {
      title: "Voorbeeld",
      description: "Scan om te testen",
      rawText: "QR-code tekst",
      downloadPNG: "Download PNG",
      qrSize: "QR-code grootte",
      share: "URL delen",
      urlCopied: "URL gekopieerd naar klembord!"
    },
    types: {
      url: "URL",
      social: "Social Media",
      vcard: "Contact (vCard)",
      phone: "Telefoon",
      sms: "SMS",
      email: "E-mail",
      wifi: "Wi-Fi",
      crypto: "Cryptovaluta",
      geo: "Locatie",
      event: "Evenement",
      text: "Tekst",
      mecard: "Contact (MeCard)"
    },
    forms: {
      urlPlaceholder: "https://voorbeeld.nl",
      firstName: "Voornaam",
      lastName: "Achternaam",
      phone: "Telefoon",
      email: "E-mail",
      organization: "Organisatie",
      organizationOptional: "Organisatie (Optioneel)",
      street: "Straat",
      streetOptional: "Straat (Optioneel)",
      city: "Stad",
      cityOptional: "Stad (Optioneel)",
      state: "Provincie",
      stateOptional: "Provincie (Optioneel)",
      zip: "Postcode",
      zipOptional: "Postcode (Optioneel)",
      country: "Land",
      countryOptional: "Land (Optioneel)",
      phoneNumber: "Telefoonnummer",
      phonePlaceholder: "+31612345678",
      message: "Bericht",
      messageOptional: "Bericht (Optioneel)",
      emailAddress: "E-mailadres",
      subject: "Onderwerp",
      subjectOptional: "Onderwerp (Optioneel)",
      body: "Inhoud",
      bodyOptional: "Inhoud (Optioneel)",
      networkName: "Netwerknaam (SSID)",
      password: "Wachtwoord",
      encryption: "Versleuteling",
      encryptionType: "Type versleuteling",
      selectEncryption: "Selecteer type versleuteling",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Geen",
      cryptocurrency: "Cryptovaluta",
      selectCrypto: "Selecteer cryptovaluta",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Wallet-adres",
      amount: "Bedrag",
      amountOptional: "Bedrag (Optioneel)",
      label: "Label",
      labelOptional: "Bericht/Label (Optioneel)",
      latitude: "Breedtegraad",
      longitude: "Lengtegraad",
      eventTitle: "Evenementtitel",
      startDate: "Startdatum",
      endDate: "Einddatum",
      description: "Beschrijving",
      descriptionOptional: "Beschrijving (Optioneel)",
      text: "Tekst",
      name: "Naam",
      emailOptional: "E-mail (Optioneel)",
      socialPlatform: "Social media platform",
      selectPlatform: "Selecteer platform",
      username: "Gebruikersnaam",
      usernamePlaceholder: "jouw_gebruikersnaam",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  sv: {
    settings: {
      title: "Inställningar",
      description: "Anpassa din QR-kod",
      type: "Typ",
      typeSelect: "Välj typ",
      colors: "Färger",
      foreground: "Förgrund",
      background: "Bakgrund",
      logo: "Logotyp",
      logoPlaceholder: "Logotyp URL",
      autoFavicon: "Använda webbplatsens favicon automatiskt",
      useSocialIcon: "Använda sociala medie-symbol",
      useCryptoIcon: "Använda sociala medie-symbol",
      size: "QR-kod storlek",
      logoSize: "Logotyp storlek",
    },
    preview: {
      title: "Förhandsvisning",
      description: "Skanna för att testa",
      rawText: "QR-kod text",
      downloadPNG: "Ladda ner PNG",
      qrSize: "QR-kod storlek",
      share: "Dela URL",
      urlCopied: "URL kopierad till urklipp!"
    },
    types: {
      url: "URL",
      social: "Sociala Medier",
      vcard: "Kontakt (vCard)",
      phone: "Telefon",
      sms: "SMS",
      email: "E-post",
      wifi: "Wi-Fi",
      crypto: "Kryptovaluta",
      geo: "Plats",
      event: "Händelse",
      text: "Text",
      mecard: "Kontakt (MeCard)"
    },
    forms: {
      urlPlaceholder: "https://exempel.se",
      firstName: "Förnamn",
      lastName: "Efternamn",
      phone: "Telefon",
      email: "E-post",
      organization: "Organisation",
      organizationOptional: "Organisation (Valfritt)",
      street: "Gatuadress",
      streetOptional: "Gatuadress (Valfritt)",
      city: "Stad",
      cityOptional: "Stad (Valfritt)",
      state: "Län",
      stateOptional: "Län (Valfritt)",
      zip: "Postnummer",
      zipOptional: "Postnummer (Valfritt)",
      country: "Land",
      countryOptional: "Land (Valfritt)",
      phoneNumber: "Telefonnummer",
      phonePlaceholder: "+46701234567",
      message: "Meddelande",
      messageOptional: "Meddelande (Valfritt)",
      emailAddress: "E-postadress",
      subject: "Ämne",
      subjectOptional: "Ämne (Valfritt)",
      body: "Innehåll",
      bodyOptional: "Innehåll (Valfritt)",
      networkName: "Nätverksnamn (SSID)",
      password: "Lösenord",
      encryption: "Kryptering",
      encryptionType: "Krypteringstyp",
      selectEncryption: "Välj krypteringstyp",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Ingen",
      cryptocurrency: "Kryptovaluta",
      selectCrypto: "Välj kryptovaluta",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Plånboksadress",
      amount: "Belopp",
      amountOptional: "Belopp (Valfritt)",
      label: "Etikett",
      labelOptional: "Meddelande/Etikett (Valfritt)",
      latitude: "Latitud",
      longitude: "Longitud",
      eventTitle: "Händelsetitel",
      startDate: "Startdatum",
      endDate: "Slutdatum",
      description: "Beskrivning",
      descriptionOptional: "Beskrivning (Valfritt)",
      text: "Text",
      name: "Namn",
      emailOptional: "E-post (Valfritt)",
      socialPlatform: "Social plattform",
      selectPlatform: "Välj plattform",
      username: "Användarnamn",
      usernamePlaceholder: "ditt_användarnamn",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  pl: {
    settings: {
      title: "Ustawienia",
      description: "Dostosuj swój kod QR",
      type: "Typ",
      typeSelect: "Wybierz typ",
      colors: "Kolory",
      foreground: "Pierwszy plan",
      background: "Tło",
      logo: "Logo",
      logoPlaceholder: "URL logo",
      autoFavicon: "Używać faviconu strony internetowej automatycznie",
      useSocialIcon: "Usar ícone de rede social",
      useCryptoIcon: "Usar ícone de criptomoeda",
      size: "Rozmiar kodu QR",
      logoSize: "Rozmiar logotypu",
    },
    preview: {
      title: "Podgląd",
      description: "Zeskanuj, aby przetestować",
      rawText: "Tekst kodu QR",
      downloadPNG: "Pobierz PNG",
      qrSize: "Rozmiar kodu QR",
      share: "Udostępnij URL",
      urlCopied: "URL skopiowany do schowka!"
    },
    types: {
      url: "URL",
      social: "Media Społecznościowe",
      vcard: "Kontakt (vCard)",
      phone: "Telefon",
      sms: "SMS",
      email: "E-mail",
      wifi: "Wi-Fi",
      crypto: "Kryptowaluta",
      geo: "Lokalizacja",
      event: "Wydarzenie",
      text: "Tekst",
      mecard: "Kontakt (MeCard)"
    },
    forms: {
      urlPlaceholder: "https://przyklad.pl",
      firstName: "Imię",
      lastName: "Nazwisko",
      phone: "Telefon",
      email: "E-mail",
      organization: "Organizacja",
      organizationOptional: "Organizacja (Opcjonalnie)",
      street: "Ulica",
      streetOptional: "Ulica (Opcjonalnie)",
      city: "Miasto",
      cityOptional: "Miasto (Opcjonalnie)",
      state: "Województwo",
      stateOptional: "Województwo (Opcjonalnie)",
      zip: "Kod pocztowy",
      zipOptional: "Kod pocztowy (Opcjonalnie)",
      country: "Kraj",
      countryOptional: "Kraj (Opcjonalnie)",
      phoneNumber: "Numer telefonu",
      phonePlaceholder: "+48501234567",
      message: "Wiadomość",
      messageOptional: "Wiadomość (Opcjonalnie)",
      emailAddress: "Adres e-mail",
      subject: "Temat",
      subjectOptional: "Temat (Opcjonalnie)",
      body: "Treść",
      bodyOptional: "Treść (Opcjonalnie)",
      networkName: "Nazwa sieci (SSID)",
      password: "Hasło",
      encryption: "Szyfrowanie",
      encryptionType: "Typ szyfrowania",
      selectEncryption: "Wybierz typ szyfrowania",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Brak",
      cryptocurrency: "Kryptowaluta",
      selectCrypto: "Wybierz kryptowalutę",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Adres portfela",
      amount: "Kwota",
      amountOptional: "Kwota (Opcjonalnie)",
      label: "Etykieta",
      labelOptional: "Wiadomość/Etykieta (Opcjonalnie)",
      latitude: "Szerokość",
      longitude: "Długość",
      eventTitle: "Tytuł wydarzenia",
      startDate: "Data rozpoczęcia",
      endDate: "Data zakończenia",
      description: "Opis",
      descriptionOptional: "Opis (Opcjonalnie)",
      text: "Tekst",
      name: "Nazwa",
      emailOptional: "E-mail (Opcjonalnie)",
      socialPlatform: "Platforma społecznościowa",
      selectPlatform: "Wybierz platformę",
      username: "Nazwa użytkownika",
      usernamePlaceholder: "twoja_nazwa",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  },
  no: {
    settings: {
      title: "Innstillinger",
      description: "Tilpass din QR-kode",
      type: "Type",
      typeSelect: "Velg type",
      colors: "Farger",
      foreground: "Forgrunn",
      background: "Bakgrunn",
      logo: "Logo",
      logoPlaceholder: "Logo URL",
      autoFavicon: "Bruke nettstedets favicon automatisk",
      useSocialIcon: "Bruke sosialt medie-symbol",
      useCryptoIcon: "Bruke sosialt medie-symbol",
      size: "QR-kode størrelse",
      logoSize: "Logo størrelse",
    },
    preview: {
      title: "Forhåndsvisning",
      description: "Skann for å teste",
      rawText: "QR-kode tekst",
      downloadPNG: "Last ned PNG",
      qrSize: "QR-kode størrelse",
      share: "Dela URL",
      urlCopied: "URL kopiert til utklippstavlen!"
    },
    types: {
      url: "URL",
      social: "Sosiale Medier",
      vcard: "Kontakt (vCard)",
      phone: "Telefon",
      sms: "SMS",
      email: "E-post",
      wifi: "Wi-Fi",
      crypto: "Kryptovaluta",
      geo: "Plassering",
      event: "Hendelse",
      text: "Tekst",
      mecard: "Kontakt (MeCard)"
    },
    forms: {
      urlPlaceholder: "https://eksempel.no",
      firstName: "Fornavn",
      lastName: "Etternavn",
      phone: "Telefon",
      email: "E-post",
      organization: "Organisasjon",
      organizationOptional: "Organisasjon (Valgfritt)",
      street: "Gateadresse",
      streetOptional: "Gateadresse (Valgfritt)",
      city: "By",
      cityOptional: "By (Valgfritt)",
      state: "Fylke",
      stateOptional: "Fylke (Valgfritt)",
      zip: "Postnummer",
      zipOptional: "Postnummer (Valgfritt)",
      country: "Land",
      countryOptional: "Land (Valgfritt)",
      phoneNumber: "Telefonnummer",
      phonePlaceholder: "+4791234567",
      message: "Melding",
      messageOptional: "Melding (Valgfritt)",
      emailAddress: "E-postadresse",
      subject: "Emne",
      subjectOptional: "Emne (Valgfritt)",
      body: "Innhold",
      bodyOptional: "Innhold (Valgfritt)",
      networkName: "Nettverksnavn (SSID)",
      password: "Passord",
      encryption: "Kryptering",
      encryptionType: "Krypteringstype",
      selectEncryption: "Velg krypteringstype",
      wpaWpa2: "WPA/WPA2",
      wep: "WEP",
      none: "Ingen",
      cryptocurrency: "Kryptovaluta",
      selectCrypto: "Velg kryptovaluta",
      bitcoin: "Bitcoin",
      ethereum: "Ethereum",
      walletAddress: "Lommebokadresse",
      amount: "Beløp",
      amountOptional: "Beløp (Valgfritt)",
      label: "Etikett",
      labelOptional: "Melding/Etikett (Valgfritt)",
      latitude: "Breddegrad",
      longitude: "Lengdegrad",
      eventTitle: "Hendelsestitel",
      startDate: "Startdato",
      endDate: "Sluttdato",
      description: "Beskrivelse",
      descriptionOptional: "Beskrivelse (Valgfritt)",
      text: "Tekst",
      name: "Navn",
      emailOptional: "E-post (Valgfritt)",
      socialPlatform: "Sosial plattform",
      selectPlatform: "Velg plattform",
      username: "Brukernavn",
      usernamePlaceholder: "ditt_brukernavn",
      platforms: {
        x: "X",
        instagram: "Instagram",
        facebook: "Facebook",
        linkedin: "LinkedIn",
        github: "GitHub",
        tiktok: "TikTok",
        youtube: "YouTube",
        twitch: "Twitch",
        bluesky: "Bluesky"
      }
    }
  }
};
